import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionItem, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Spinner, Button, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import * as Babel from '@babel/standalone';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { useLazyGetAiWorkerByIdQuery, useGetAiWorkerUsageByIdMutation } from '@circuitry-ai/doc-data';
import { openUxGenieTemplateInNewTab } from '../utils/commonutils';
import { useUxgenieTemplateHtmlResolver } from '../template/useUxgenieTemplateHtmlResolver';
import { ApplicationType, applicationType } from '../utils/constants';
import { extractClaimIntakeData, extractRendererData } from './rendererUtils';
import { getActionOutputV2Payload } from './actionOutputV2Utils';
import { RendererHeader } from './RendererHeader';
import { RendererTabs } from './RendererTabs';
import { FullScreenModal } from './FullScreenModal';
import { MarkdownContent } from './AISummaryAccordion';
import { ScoreCard } from './ScoreCard';
import { useScoreColor, useRenderScoreChip } from './claimScoreRendererShared';
import { parseScore } from '../utils/dateutils';
import {
    buildClaimIntakeActionSections,
    getClaimIntakeSectionIcon,
    renderClaimIntakeSectionContent,
} from './claimIntakeActionOutputSections';
import { extractEstimateParserResultsFromUsageData } from './claimIntakeActionOutputFormatters';

const CSG_ACCORDION_ITEM_CLASSNAMES = {
    content: 'pl-1 pr-0',
    base: 'w-full !px-2 !shadow-none border-2',
    title: 'text-base',
} as const;

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function unwrapTemplateResponse(response: unknown): Record<string, unknown> | null {
    if (Array.isArray(response) && response.length > 0) {
        return asRecord(response[0]);
    }
    const record = asRecord(response);
    if (record?.data) {
        const inner = asRecord(record.data);
        if (inner) return inner;
    }
    return record;
}

interface ClaimIntakeSourcesSectionProps {
    data: Record<string, unknown> | null | undefined;
    pdfSourcesComponent?: React.ReactNode;
}

const ClaimIntakeSourcesSection: React.FC<ClaimIntakeSourcesSectionProps> = ({ data, pdfSourcesComponent }) => {
    const { t } = useTranslation();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isTemplateModalFullscreen, setIsTemplateModalFullscreen] = useState(false);
    const [isParserJsonModalOpen, setIsParserJsonModalOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateFullData, setTemplateFullData] = useState<Record<string, unknown> | null>(null);
    const [resolvedTemplateHtml, setResolvedTemplateHtml] = useState<string | null>(null);

    const [getAiWorkerById, { isFetching: isFetchingAgent }] = useLazyGetAiWorkerByIdQuery();
    const [getTemplateById, { isLoading: isLoadingTemplate }] = useGetAiWorkerUsageByIdMutation();
    const { resolveHtml } = useUxgenieTemplateHtmlResolver();

    const entityNumber = (data?.entity_number as string) || 'Unknown';
    const agentName = (data?.agent_name as string) || '';
    const claimIntakeData = extractClaimIntakeData(data?.action_output);
    const hasClaimIntakeData = !!claimIntakeData && Object.keys(claimIntakeData).length > 0;
    const parserResults = useMemo(
        () => extractEstimateParserResultsFromUsageData(data ?? undefined),
        [data]
    );
    const parserJsonText = parserResults ? JSON.stringify(parserResults, null, 2) : '';

    useEffect(() => {
        const aiworkerId = data?.aiworker_id;
        if (!aiworkerId || typeof aiworkerId !== 'string') return;
        getAiWorkerById(aiworkerId)
            .then((result: { data?: { aiworkers_attrs?: Array<{ attr_name?: string; attr_value?: string }> } }) => {
                const agent = result?.data;
                if (!agent) return;
                const attrs = agent?.aiworkers_attrs ?? [];
                const uxgenieAttr = attrs.find((a) => a?.attr_name === 'uxgenie_template');
                const templateId = uxgenieAttr?.attr_value?.trim();
                if (templateId) setSelectedTemplateId(templateId);
            })
            .catch((err: unknown) => console.error('Failed to fetch agent for UXGenie template:', err));
    }, [data?.aiworker_id, getAiWorkerById]);

    useEffect(() => {
        if (!selectedTemplateId) return;
        getTemplateById(selectedTemplateId)
            .unwrap()
            .then((response: unknown) => {
                setTemplateFullData(unwrapTemplateResponse(response));
            })
            .catch((err: unknown) => console.error('Failed to fetch template data:', err));
    }, [selectedTemplateId, getTemplateById]);

    // If the template's HTML was saved to S3 (WAF blocks large inline HTML), resolve
    // it back to raw HTML text once the full template record has loaded.
    useEffect(() => {
        const outputData = (templateFullData?.action_output ?? templateFullData?.output_data) as
            { generated_code?: string; output_type?: string; storage?: string; html_file_name?: string } | undefined;
        if (outputData?.generated_code || outputData?.output_type !== 'html' || outputData?.storage !== 's3') {
            setResolvedTemplateHtml(null);
            return;
        }
        const aiworker_id = templateFullData?.aiworker_id as string | undefined;
        const aiworker_usage_id = (templateFullData?.aiworker_usage_id as string | undefined) || selectedTemplateId;
        if (!aiworker_id || !aiworker_usage_id) return;

        let cancelled = false;
        resolveHtml({ aiworker_id, aiworker_usage_id, fileName: outputData.html_file_name })
            .then((html) => { if (!cancelled) setResolvedTemplateHtml(html); })
            .catch((error) => console.error('Failed to resolve saved HTML template from storage:', error));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateFullData, selectedTemplateId]);

    const openTemplateInNewTab = async () => {
        const generatedCode =
            (templateFullData?.action_output as { generated_code?: string } | undefined)?.generated_code ||
            (templateFullData?.output_data as { generated_code?: string } | undefined)?.generated_code ||
            resolvedTemplateHtml;
        if (!generatedCode || !claimIntakeData) {
            CtyShowToast(checkType.error, t('Template or claim data is missing'));
            return;
        }
        await openUxGenieTemplateInNewTab(
            { generatedCode, receivedClaimData: claimIntakeData, agentName },
            {
                onSessionStorageError: () =>
                    CtyShowToast(checkType.error, t('Could not open in new tab')),
            }
        );
    };

    const handleEntityNumberClick = () => {
        if (!hasClaimIntakeData) {
            CtyShowToast(checkType.error, t('No claim intake data available.'));
            return;
        }
        if (!selectedTemplateId) {
            CtyShowToast(checkType.error, t('No template configured. Please add a UXGenie template in action settings.'));
            return;
        }
        if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
            void openTemplateInNewTab();
        } else {
            setIsTemplateModalOpen(true);
        }
    };

    const renderTemplatePreview = (isFullscreen = false) => {
        const outputDataForLoadingCheck = (templateFullData?.action_output ?? templateFullData?.output_data) as
            { generated_code?: string; output_type?: string; storage?: string } | undefined;
        const isAwaitingHtmlResolution = !!templateFullData
            && !outputDataForLoadingCheck?.generated_code
            && outputDataForLoadingCheck?.output_type === 'html'
            && outputDataForLoadingCheck?.storage === 's3'
            && !resolvedTemplateHtml;

        if (isLoadingTemplate || isAwaitingHtmlResolution) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Spinner size="lg" className="mb-4" />
                    <p>{t('Loading template...')}</p>
                </div>
            );
        }

        const generatedCode =
            (templateFullData?.action_output as { generated_code?: string } | undefined)?.generated_code ||
            (templateFullData?.output_data as { generated_code?: string } | undefined)?.generated_code ||
            resolvedTemplateHtml;

        if (!generatedCode) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Icon icon="mdi:file-document-remove-outline" width={48} className="mb-4 text-default-400" />
                    <p>{selectedTemplateId ? t('Template data not loaded yet') : t('No template selected')}</p>
                </div>
            );
        }

        if (!hasClaimIntakeData) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Icon icon="mdi:file-document-remove-outline" width={48} className="mb-4 text-default-400" />
                    <p>{t('No claim intake data available for template')}</p>
                </div>
            );
        }

        try {
            const cleanedCode = generatedCode
                .replace(/\?\?\s*['"]N\/A['"]/g, "?? ''")
                .replace(/\?\?\s*['"]-['"]/g, "?? ''");

            const codeToTranspile = `(function(data) { return (${cleanedCode}); })`;
            const transpiled = Babel.transform(codeToTranspile, {
                presets: ['react'],
                filename: 'template.jsx',
            }).code;

            // eslint-disable-next-line no-new-func
            const renderFunction = new Function('React', 'console', 'return ' + transpiled)(React, console);
            const rendered = renderFunction(claimIntakeData);

            return (
                <div
                    className={
                        isFullscreen
                            ? 'bg-white rounded-lg border border-default-200 p-4 overflow-auto min-h-0 flex-1 w-full max-w-full'
                            : 'bg-white rounded-lg border border-default-200 p-4 overflow-auto max-h-[600px] w-full max-w-full'
                    }
                >
                    <div className="w-full max-w-full overflow-x-auto">{rendered}</div>
                </div>
            );
        } catch (error) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-red-500">
                    <Icon icon="mdi:alert-circle" width={48} className="mb-4" />
                    <p className="font-semibold">{t('Template Rendering Error')}</p>
                    <p className="text-sm text-default-500 mt-2">{(error as Error).message}</p>
                </div>
            );
        }
    };

    return (
        <div className="w-full flex flex-col gap-2">
            {hasClaimIntakeData && (
                <div className="flex">
                    <CtyButton
                        color="default"
                        variant="light"
                        isLoading={isFetchingAgent || isLoadingTemplate}
                        onPress={handleEntityNumberClick}
                        startContent={<Icon icon="mdi:eye" width={16} />}
                        className="hover:bg-transparent"
                    >
                        {entityNumber}
                    </CtyButton>
                </div>
            )}

            {parserResults ? (
                <div className="flex">
                    <CtyButton
                        size="sm"
                        variant="flat"
                        color="default"
                        className="font-medium"
                        startContent={<Icon icon="mdi:code-json" width={16} />}
                        onPress={() => setIsParserJsonModalOpen(true)}
                    >
                        Show Parser JSON
                    </CtyButton>
                </div>
            ) : null}

            {pdfSourcesComponent ? <div className="mt-2">{pdfSourcesComponent}</div> : null}

            <Modal
                isOpen={isParserJsonModalOpen}
                onClose={() => setIsParserJsonModalOpen(false)}
                size="3xl"
                placement="center"
                scrollBehavior="inside"
                classNames={{ backdrop: 'bg-black/50' }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 border-b border-default-200">
                        <span className="text-base font-semibold text-foreground">Estimate Parser Response</span>
                        <span className="text-sm font-normal text-default-500">Raw JSON returned from the estimate parser.</span>
                    </ModalHeader>
                    <ModalBody className="py-4">
                        <pre className="max-h-[min(70vh,560px)] overflow-auto rounded-lg border border-default-200 bg-content2 p-4 text-xs font-mono leading-relaxed text-default-700 whitespace-pre-wrap break-words">
                            {parserJsonText}
                        </pre>
                    </ModalBody>
                    <ModalFooter className="border-t border-default-200">
                        <CtyButton color="primary" variant="flat" onPress={() => setIsParserJsonModalOpen(false)}>
                            Close
                        </CtyButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={isTemplateModalOpen}
                onOpenChange={(open) => {
                    setIsTemplateModalOpen(open);
                    if (!open) setIsTemplateModalFullscreen(false);
                }}
                hideCloseButton
                size={isTemplateModalFullscreen ? 'full' : undefined}
                classNames={{
                    wrapper: isTemplateModalFullscreen
                        ? 'p-0 w-screen h-screen max-w-none max-h-none min-w-full min-h-full'
                        : undefined,
                    base: isTemplateModalFullscreen
                        ? 'w-full h-full max-w-none max-h-none rounded-none m-0 flex flex-col'
                        : 'max-w-[70vw] max-h-[70vh]',
                    body: isTemplateModalFullscreen ? 'flex-1 min-h-0 overflow-auto p-0' : 'p-0',
                    header: 'border-b border-default-200',
                }}
                scrollBehavior="inside"
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <Icon icon="mdi:file-code" className="text-primary" width={28} />
                                        <div>
                                            <h2 className="text-xl font-semibold">{agentName}</h2>
                                            <p className="text-sm text-default-500 font-normal">{entityNumber}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isLoadingTemplate ? <Spinner size="sm" /> : null}
                                        <Tooltip
                                            content={isTemplateModalFullscreen ? t('Minimize') : t('Fullscreen')}
                                            placement="bottom"
                                            showArrow
                                        >
                                            <Button
                                                isIconOnly
                                                variant="light"
                                                size="sm"
                                                onClick={() => setIsTemplateModalFullscreen((prev) => !prev)}
                                                aria-label={isTemplateModalFullscreen ? t('Minimize') : t('Fullscreen')}
                                            >
                                                <Icon
                                                    icon={isTemplateModalFullscreen ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'}
                                                    className="w-5 h-5"
                                                />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content={t('Open in new tab')} placement="bottom" showArrow>
                                            <Button
                                                isIconOnly
                                                variant="light"
                                                size="sm"
                                                onClick={openTemplateInNewTab}
                                                aria-label={t('Open in new tab')}
                                            >
                                                <Icon icon="mdi:open-in-new" className="w-5 h-5" />
                                            </Button>
                                        </Tooltip>
                                        {isTemplateModalFullscreen ? null : (
                                            <Button isIconOnly variant="light" onClick={onClose} aria-label={t('Close')}>
                                                <Icon icon="mdi:close" className="w-5 h-5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </ModalHeader>
                            <ModalBody className={isTemplateModalFullscreen ? 'p-6 flex-1 min-h-0 overflow-auto flex flex-col' : 'p-6'}>
                                <div className={isTemplateModalFullscreen ? 'flex-1 min-h-0 overflow-auto' : undefined}>
                                    {renderTemplatePreview(isTemplateModalFullscreen)}
                                </div>
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export interface ClaimIntakeAgentRendererProps {
    claimData: unknown;
    onButtonAction?: (action: string, buttonData: unknown) => void;
    showActionButtons?: boolean;
    feedbackComponent?: React.ReactNode;
    knowledgeComponent?: React.ReactNode;
    onRefresh?: () => void;
    className?: string;
    isModal?: boolean;
    /** When provided, "Full View" delegates to this instead of the built-in modal (e.g. widget host opening a real fullscreen tab). */
    onRequestFullScreen?: () => void;
}

const ClaimIntakeAgentRendererInner: React.FC<ClaimIntakeAgentRendererProps> = ({
    claimData,
    onButtonAction,
    showActionButtons = true,
    feedbackComponent,
    knowledgeComponent,
    onRefresh,
    className = '',
    isModal = false,
    onRequestFullScreen,
}) => {
    const { t } = useTranslation();
    // Agent-config driven score color (falls back to 85/60 defaults when no provider/config).
    const getScoreColor = useScoreColor();
    const renderScoreChip = useRenderScoreChip();
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [isExpanded, setIsExpanded] = useState(false);

    const { data, duration, entityNumber, timestamp, aiSummary, agentName, agentScore } = useMemo(
        () => extractRendererData(claimData),
        [claimData]
    );

    const claimScoreV2 = useMemo(
        () => getActionOutputV2Payload(data?.action_output),
        [data?.action_output]
    );
    const effectiveScore = claimScoreV2?.score ?? agentScore;
    const scoreValue = parseScore(effectiveScore);
    const scoreColor = getScoreColor(scoreValue);
    const displayScore =
        effectiveScore != null && effectiveScore !== '' && effectiveScore !== 'N/A'
            ? `${Math.round(scoreValue)}%`
            : null;

    const sections = useMemo(
        () => buildClaimIntakeActionSections(data?.action_output as Record<string, unknown> | undefined),
        [data?.action_output]
    );

    const usageRowId =
        (data as { aiworker_usage_id?: string } | undefined)?.aiworker_usage_id ?? entityNumber ?? '';

    useEffect(() => {
        setSelectedKeys(new Set());
        setIsExpanded(false);
    }, [usageRowId]);

    const allSectionKeys = useMemo(() => {
        const keys = new Set<string>();
        if (aiSummary) keys.add('ai-summary');
        for (const section of sections) keys.add(section.key);
        if (!aiSummary && sections.length === 0) keys.add('no-details');
        return keys;
    }, [aiSummary, sections]);

    const handleExpandAll = useCallback(() => {
        if (isExpanded) {
            setSelectedKeys(new Set());
            setIsExpanded(false);
        } else {
            setSelectedKeys(new Set(allSectionKeys));
            setIsExpanded(true);
        }
    }, [allSectionKeys, isExpanded]);

    const overviewSections = useMemo(() => {
        const items: React.ReactElement[] = [];

        if (aiSummary) {
            items.push(
                <AccordionItem
                    key="ai-summary"
                    aria-label={t('AI Summary')}
                    title={
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-pink-100 rounded-lg">
                                <Icon icon="mdi:sparkles" className="text-pink-600" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                            </div>
                            <div>
                                <div className="renderer-text-primary font-semibold">{t('AI Summary')}</div>
                                <div className="renderer-text-secondary">{t('AI-generated analysis overview')}</div>
                            </div>
                        </div>
                    }
                    className="mb-3"
                    classNames={CSG_ACCORDION_ITEM_CLASSNAMES}
                >
                    <div className="pt-1">
                        <MarkdownContent content={aiSummary} />
                    </div>
                </AccordionItem>
            );
        }

        for (const section of sections) {
            const meta = getClaimIntakeSectionIcon(section.title);
            const sectionData = asRecord(section.data);
            const sectionScore = sectionData?.score;
            const showScoreChip = sectionScore !== undefined && sectionScore !== null && sectionScore !== '';            items.push(
                <AccordionItem
                    key={section.key}
                    aria-label={section.title}
                    title={
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 ${meta.bg} rounded-lg`}>
                                    <Icon icon={meta.icon} className={meta.color} style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                                </div>
                                <div>
                                    <div className="renderer-text-primary font-semibold capitalize">{section.title}</div>
                                    <div className="renderer-text-secondary">{meta.subtitle}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {showScoreChip ? renderScoreChip(sectionScore) : null}
                                {section.error ? (
                                    <Icon icon="mdi:alert-circle" className="w-4 h-4 text-red-600 shrink-0" />
                                ) : null}
                            </div>
                        </div>
                    }
                    className="mb-3"
                    classNames={CSG_ACCORDION_ITEM_CLASSNAMES}
                >
                    <div className="pt-2 min-w-0 max-w-full overflow-hidden">
                        {renderClaimIntakeSectionContent(section.title, section.data)}
                    </div>
                </AccordionItem>
            );
        }

        if (!aiSummary && sections.length === 0) {
            items.push(
                <AccordionItem
                    key="no-details"
                    aria-label={t('No Details')}
                    title={
                        <div className="flex items-center gap-3">
                            <Icon icon="mdi:information-outline" className="w-5 h-5 text-default-500" />
                            <span className="font-medium text-default-500">{t('No Details Available')}</span>
                        </div>
                    }
                    className="mb-3"
                    classNames={CSG_ACCORDION_ITEM_CLASSNAMES}
                >
                    <div className="text-center py-8 text-default-500 text-sm">{t('No details available')}</div>
                </AccordionItem>
            );
        }

        return items;
    }, [aiSummary, sections, t]);

    if (!data) {
        return (
            <div className={`flex items-center justify-center py-12 ${className}`}>
                <div className="text-default-500">{t('No claim data available')}</div>
            </div>
        );
    }

    const actionButtons = Array.isArray(data.action_button) ? data.action_button : [];

    const overviewContent = (
        <Accordion
            variant="splitted"
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={(keys) => {
                const next = keys instanceof Set ? (keys as Set<string>) : new Set<string>();
                setSelectedKeys(next);
                setIsExpanded(next.size >= allSectionKeys.size && allSectionKeys.size > 0);
            }}
            isCompact
            className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
        >
            {overviewSections}
        </Accordion>
    );

    const sourcesNode = (
        <ClaimIntakeSourcesSection data={data} pdfSourcesComponent={knowledgeComponent} />
    );

    let headerScoreBlock: React.ReactNode = null;
    if (displayScore) {
        headerScoreBlock = (
            <div className="flex-none pl-3 pr-3">
                <ScoreCard
                    displayScore={displayScore}
                    scoreValue={scoreValue}
                    scoreColor={scoreColor}
                    showMatchBreakdown={false}
                    matchBreakdown={{ matches: 0, partial: 0, mismatches: 0 }}
                    activeFilter={null}
                    onFilterClick={() => {}}
                    feedbackComponent={feedbackComponent}
                />
            </div>
        );
    } else if (feedbackComponent) {
        headerScoreBlock = <div className="px-3 pb-3">{feedbackComponent}</div>;
    }

    return (
        <>
            <div className={`flex flex-col h-full ${className}`}>
                <div className="border-b-3 border-default-200 bg-default-100 m-2 rounded-md">
                    <RendererHeader
                        agentName={agentName}
                        duration={duration}
                        entityNumber={entityNumber}
                        timestamp={timestamp}
                        isModal={isModal}
                        onFullScreenClick={() => onRequestFullScreen ? onRequestFullScreen() : setIsFullScreenOpen(true)}
                        onRefresh={onRefresh}
                    />
                    {headerScoreBlock}
                </div>

                <RendererTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    isExpanded={isExpanded}
                    onExpandAll={handleExpandAll}
                    knowledgeComponent={sourcesNode}
                    overviewContent={overviewContent}
                />

                {showActionButtons ? (
                    <div className="flex-none p-4 border-t border-default-200 bg-content1">
                        {actionButtons.length ? (
                            <div className="flex gap-3">
                                {actionButtons.map((button: { id?: string; color?: string; action?: string; label?: string }) => (
                                    <CtyButton
                                        key={button.id ?? button.label}
                                        color={(button.color as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger') ?? 'primary'}
                                        variant="solid"
                                        className="flex-1"
                                        onPress={() => onButtonAction?.(button.action ?? '', data)}
                                    >
                                        {button.label}
                                    </CtyButton>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-default-500 text-sm">{t('No actions available')}</div>
                        )}
                    </div>
                ) : null}
            </div>

            {isModal ? null : (
                <FullScreenModal
                    isOpen={isFullScreenOpen}
                    onClose={() => setIsFullScreenOpen(false)}
                    agentName={agentName}
                    headerBorder
                >
                    <ClaimIntakeAgentRendererInner
                        claimData={claimData}
                        onButtonAction={onButtonAction}
                        showActionButtons={showActionButtons}
                        feedbackComponent={feedbackComponent}
                        knowledgeComponent={knowledgeComponent}
                        onRefresh={onRefresh}
                        isModal
                    />
                </FullScreenModal>
            )}
        </>
    );
};

export const ClaimIntakeAgentRenderer: React.FC<ClaimIntakeAgentRendererProps> = (props) => {
    const usageRowId =
        (Array.isArray(props.claimData) ? props.claimData[0]?.aiworker_usage_id : null) ??
        (props.claimData as { aiworker_usage_id?: string } | undefined)?.aiworker_usage_id ??
        'claim-intake';

    return <ClaimIntakeAgentRendererInner key={usageRowId} {...props} />;
};

export default ClaimIntakeAgentRenderer;
